import React from 'react';
import { Box, MenuItem, Select, TextField, Typography } from '@mui/material';
import { Control, Controller, FieldErrors, UseFormRegister } from 'react-hook-form';
import { CLINIC_DIALOG_SX } from '../../constants/popupStyles';

/**
 * Rendering and validation for tenant-configured fields.
 *
 * Extracted from PatientFormDialog when leads gained the same mechanism.
 * Everything here is deliberately structural rather than tied to
 * PatientFieldConfig or LeadFieldConfig: the two tables are the same shape
 * apart from the patient one's patient_type_id, and one copy of the renderer
 * is what stops them drifting the way the two config routers on the backend
 * already had.
 *
 * Form-field naming is the contract with the caller: every value is registered
 * as `additional_fields.<key>`, and errors are read from
 * `errors.additional_fields[key]`. A caller that splits additional_fields back
 * off the payload on submit works for both entities unchanged.
 */

export interface DynamicFieldConfig {
  key: string;
  label: string;
  field_type: string;   // text | number | date | bool | single_select
  options: { value: string; label: string }[] | null;
  display_in_overview: boolean;
  display_in_edit: boolean;
  display_condition: { field: string; values: string[] } | null;
  default_value: any | null;
  is_required: boolean;
  validation: Record<string, any> | null;
  edit_tab_name: string | null;
  edit_tab_order: number;
  sort_order: number;
}

const textFieldInputProps = { style: CLINIC_DIALOG_SX.textField as React.CSSProperties };

export const buildDynamicFieldRules = (config: DynamicFieldConfig) => {
  const rules: Record<string, any> = {};
  if (config.is_required) {
    rules.required = `${config.label} is required`;
  }
  const v = config.validation;
  if (!v) return rules;
  const errMsg = v.error_message;
  if (config.field_type === 'text') {
    if (v.min_length != null) rules.minLength = { value: v.min_length, message: errMsg || `Minimum ${v.min_length} characters` };
    if (v.max_length != null) rules.maxLength = { value: v.max_length, message: errMsg || `Maximum ${v.max_length} characters` };
    if (v.regex) {
      try { rules.pattern = { value: new RegExp(v.regex), message: errMsg || 'Invalid format' }; } catch {}
    }
  }
  if (config.field_type === 'number') {
    if (v.min_value != null) rules.min = { value: v.min_value, message: errMsg || `Minimum value is ${v.min_value}` };
    if (v.max_value != null) rules.max = { value: v.max_value, message: errMsg || `Maximum value is ${v.max_value}` };
  }
  if (config.field_type === 'date' && v.type) {
    rules.validate = (val: string) => {
      if (!val) return true;
      const date = new Date(val);
      const now = new Date();
      const msg = errMsg || 'Invalid date';
      if (v.type === 'past_date') return date < now || msg;
      if (v.type === 'future_date') return date > now || msg;
      if (v.type === 'within_x_days' && v.value != null) {
        const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - v.value);
        return date >= cutoff || msg;
      }
      if (v.type === 'min_age' && v.value != null) {
        const minBirth = new Date(); minBirth.setFullYear(minBirth.getFullYear() - v.value);
        return date <= minBirth || msg;
      }
      return true;
    };
  }
  return rules;
};

export const DynamicField: React.FC<{
  config: DynamicFieldConfig;
  register: UseFormRegister<any>;
  control: Control<any>;
  defaultValue?: any;
  errors?: FieldErrors;
}> = ({ config, register, control, defaultValue, errors }) => {
  const fieldName = `additional_fields.${config.key}`;
  const labelSx = { ...CLINIC_DIALOG_SX.fieldLabel, minWidth: '140px' };
  const rules = buildDynamicFieldRules(config);
  // Normalize stored value to string for Select fields
  const selectDefault = defaultValue === true ? 'true' : defaultValue === false ? 'false' : (defaultValue ?? '');
  const fieldError = (errors?.additional_fields as any)?.[config.key];
  const errorMsg = fieldError?.message as string | undefined;

  if (config.field_type === 'text') {
    return (
      <Box sx={CLINIC_DIALOG_SX.fieldRow}>
        <Typography variant="body2" sx={labelSx}>{config.label}</Typography>
        <TextField {...register(fieldName, rules)} fullWidth size="small" error={!!errorMsg} helperText={errorMsg} InputProps={textFieldInputProps} />
      </Box>
    );
  }
  if (config.field_type === 'number') {
    return (
      <Box sx={CLINIC_DIALOG_SX.fieldRow}>
        <Typography variant="body2" sx={labelSx}>{config.label}</Typography>
        <TextField {...register(fieldName, { ...rules, valueAsNumber: true })} fullWidth size="small" type="number" error={!!errorMsg} helperText={errorMsg} InputProps={textFieldInputProps} />
      </Box>
    );
  }
  if (config.field_type === 'date') {
    return (
      <Box sx={CLINIC_DIALOG_SX.fieldRow}>
        <Typography variant="body2" sx={labelSx}>{config.label}</Typography>
        <TextField {...register(fieldName, rules)} fullWidth size="small" type="date" error={!!errorMsg} helperText={errorMsg} InputProps={textFieldInputProps} />
      </Box>
    );
  }
  if (config.field_type === 'bool') {
    return (
      <Box sx={CLINIC_DIALOG_SX.fieldRow}>
        <Typography variant="body2" sx={labelSx}>{config.label}</Typography>
        <Controller
          name={fieldName}
          control={control}
          defaultValue={selectDefault}
          rules={config.is_required ? { validate: (v) => (v !== '' && v !== null && v !== undefined) || `${config.label} is required` } : undefined}
          render={({ field, fieldState }) => {
            const displayValue = field.value === true ? 'true' : field.value === false ? 'false' : (field.value ?? '');
            return (
              <Box sx={{ flex: 1 }}>
                <Select {...field} value={displayValue} fullWidth size="small" displayEmpty sx={{ ...CLINIC_DIALOG_SX.select, ...(fieldState.error ? { '.MuiOutlinedInput-notchedOutline': { borderColor: '#d32f2f' } } : {}) }}>
                  <MenuItem value=""><em>Not set</em></MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
                {fieldState.error && <Typography variant="caption" sx={{ color: '#d32f2f', ml: 1 }}>{fieldState.error.message}</Typography>}
              </Box>
            );
          }}
        />
      </Box>
    );
  }
  if (config.field_type === 'single_select' && config.options) {
    return (
      <Box sx={CLINIC_DIALOG_SX.fieldRow}>
        <Typography variant="body2" sx={labelSx}>{config.label}</Typography>
        <Controller
          name={fieldName}
          control={control}
          defaultValue={selectDefault}
          rules={config.is_required ? { validate: (v) => !!v || `${config.label} is required` } : undefined}
          render={({ field, fieldState }) => (
            <Box sx={{ flex: 1 }}>
              <Select {...field} value={field.value ?? ''} fullWidth size="small" displayEmpty sx={{ ...CLINIC_DIALOG_SX.select, ...(fieldState.error ? { '.MuiOutlinedInput-notchedOutline': { borderColor: '#d32f2f' } } : {}) }}>
                <MenuItem value=""><em>Not set</em></MenuItem>
                {config.options!.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
              {fieldState.error && <Typography variant="caption" sx={{ color: '#d32f2f', ml: 1 }}>{fieldState.error.message}</Typography>}
            </Box>
          )}
        />
      </Box>
    );
  }
  return null;
};

export interface DynamicTab<T extends DynamicFieldConfig> {
  name: string;
  order: number;
  configs: T[];
}

/**
 * Split configs into the tabs the dialog should show.
 *
 * A config whose edit_tab_name matches one of the dialog's own hard-coded tabs
 * is appended to that tab instead of creating a second one with the same name;
 * everything else becomes a tab of its own. Fields sort by edit_tab_order
 * within a tab, and a tab's position is the lowest order among its fields, so
 * an admin orders tabs and fields with one number.
 */
export function groupConfigsIntoTabs<T extends DynamicFieldConfig>(
  configs: T[],
  staticTabLabels: Record<string, string>,
): { staticExtensions: Map<string, T[]>; dynamicTabs: DynamicTab<T>[] } {
  const staticExtensions = new Map<string, T[]>();
  const tabMap = new Map<string, T[]>();

  for (const cfg of configs) {
    const tabName = cfg.edit_tab_name!;
    const staticKey = staticTabLabels[tabName];
    if (staticKey) {
      if (!staticExtensions.has(staticKey)) staticExtensions.set(staticKey, []);
      staticExtensions.get(staticKey)!.push(cfg);
    } else {
      if (!tabMap.has(tabName)) tabMap.set(tabName, []);
      tabMap.get(tabName)!.push(cfg);
    }
  }

  const byOrder = (a: T, b: T) => (a.edit_tab_order ?? 0) - (b.edit_tab_order ?? 0);
  staticExtensions.forEach((cfgs, key) => staticExtensions.set(key, [...cfgs].sort(byOrder)));

  const dynamicTabs = Array.from(tabMap.entries())
    .map(([name, cfgs]) => ({
      name,
      order: Math.min(...cfgs.map(c => c.edit_tab_order ?? 0)),
      configs: [...cfgs].sort(byOrder),
    }))
    .sort((a, b) => a.order - b.order);

  return { staticExtensions, dynamicTabs };
}

/**
 * Whether a field should be shown given the entity's current status.
 *
 * Only `status` conditions exist today - display_condition is {field, values}
 * and nothing sets `field` to anything else - but reading it rather than
 * assuming it is what lets a second condition type be added without every
 * caller needing to change.
 */
export function passesDisplayCondition(
  config: DynamicFieldConfig,
  currentStatus: string | undefined,
): boolean {
  const condition = config.display_condition;
  if (!condition || condition.field !== 'status') return true;
  return condition.values.includes(currentStatus ?? '');
}
